/**
 * React Query hooks for spending analytics by category (WP-CAT-12)
 *
 * Provides hooks for:
 * - Fetching spending data grouped by category
 * - Period comparison for trend indicators
 *
 * Pattern: Follows useCategoryPreferences.ts query key factory pattern
 * API Source: services/core/app/api/v1/transactions.py (spending-by-category endpoint)
 */
import { useQuery } from '@tanstack/react-query';
import { transactionsService, type SpendingByCategoryParams } from '@/services/transactions';

// ============================================================================
// Query Key Factory
// ============================================================================

/**
 * Query keys for spending analytics.
 *
 * Key structure:
 * - ['spending'] - all spending queries
 * - ['spending', 'by-category', params] - spending by category with date range
 */
export const spendingKeys = {
  all: ['spending'] as const,
  byCategory: () => [...spendingKeys.all, 'by-category'] as const,
  byCategoryParams: (params: SpendingByCategoryParams) =>
    [...spendingKeys.byCategory(), params] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch spending aggregated by category for a date range.
 *
 * @param params - Date range and optional filters
 * @returns Query result with spending data by category
 *
 * API: GET /transactions/spending-by-category
 */
export function useSpendingByCategory(params: SpendingByCategoryParams) {
  return useQuery({
    queryKey: spendingKeys.byCategoryParams(params),
    queryFn: () => transactionsService.getSpendingByCategory(params),
    enabled: !!params.startDate && !!params.endDate,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
}

/**
 * Fetch spending for current and previous period for trend comparison.
 *
 * This fetches two periods worth of data:
 * 1. Current period (specified by params)
 * 2. Previous period (same duration, immediately before)
 *
 * @param params - Current period date range
 * @returns Query results for both periods
 */
export function useSpendingWithTrend(params: SpendingByCategoryParams) {
  // Helper to format date as YYYY-MM-DD
  const formatDate = (d: Date): string => d.toISOString().split('T')[0] ?? '';

  // Calculate previous period dates
  const startDate = new Date(params.startDate);
  const endDate = new Date(params.endDate);
  const durationMs = endDate.getTime() - startDate.getTime();

  const prevEndDate = new Date(startDate.getTime() - 1); // Day before current start
  const prevStartDate = new Date(prevEndDate.getTime() - durationMs);

  const prevParams: SpendingByCategoryParams = {
    ...params,
    startDate: formatDate(prevStartDate),
    endDate: formatDate(prevEndDate),
  };

  // Current period query
  const currentQuery = useSpendingByCategory(params);

  // Previous period query (for trend comparison)
  const previousQuery = useSpendingByCategory(prevParams);

  return {
    current: currentQuery,
    previous: previousQuery,
    isLoading: currentQuery.isLoading || previousQuery.isLoading,
    isError: currentQuery.isError,
    // Helper to calculate trend percentage
    getTrend: (categoryId: string): number | null => {
      if (!currentQuery.data || !previousQuery.data) return null;

      const currentItem = currentQuery.data.find((c) => c.categoryId === categoryId);
      const previousItem = previousQuery.data.find((c) => c.categoryId === categoryId);

      if (!currentItem || !previousItem || previousItem.total === 0) return null;

      return ((currentItem.total - previousItem.total) / previousItem.total) * 100;
    },
  };
}

// ============================================================================
// Date Range Helpers
// ============================================================================

export type DateRangePreset = 'last30' | 'last90' | 'thisYear' | 'lastYear' | 'custom';

/**
 * Get date range for a preset.
 */
export function getDateRangeFromPreset(
  preset: DateRangePreset,
  customStart?: string,
  customEnd?: string
): { startDate: string; endDate: string } {
  const today = new Date();
  const formatDate = (d: Date): string => {
    const isoString = d.toISOString();
    const parts = isoString.split('T');
    return parts[0] ?? isoString.substring(0, 10);
  };

  switch (preset) {
    case 'last30': {
      const start = new Date(today);
      start.setDate(start.getDate() - 30);
      return { startDate: formatDate(start), endDate: formatDate(today) };
    }
    case 'last90': {
      const start = new Date(today);
      start.setDate(start.getDate() - 90);
      return { startDate: formatDate(start), endDate: formatDate(today) };
    }
    case 'thisYear': {
      const start = new Date(today.getFullYear(), 0, 1);
      return { startDate: formatDate(start), endDate: formatDate(today) };
    }
    case 'lastYear': {
      const start = new Date(today.getFullYear() - 1, 0, 1);
      const end = new Date(today.getFullYear() - 1, 11, 31);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    }
    case 'custom':
      return {
        startDate: customStart || formatDate(today),
        endDate: customEnd || formatDate(today),
      };
    default:
      return { startDate: formatDate(today), endDate: formatDate(today) };
  }
}
